/**
 * PATH: packages/job-hub/src/jobs/tabs/job-qa/useAttachQuotesToJob.ts
 * FILE_ID: JOBHUB-QA-ATTACH-HOOK-001
 * PURPOSE: Attach a file, QDrive record, or builder quote to the open job.
 * WHY: The New Job upload dialog creates a job. This job already exists.
 */
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { useJobHubDataAdapter } from '../../jobHubDataAdapter'
import { qaErrorMessage } from './qaTabSelectors'

function skippedMessage(data: unknown): string | null {
  const skipped = (data as { skipped?: Array<{ quote_number?: string; reason?: string }> } | null)?.skipped
  if (!Array.isArray(skipped) || skipped.length === 0) return null
  return skipped
    .map((row) => [row.quote_number, row.reason].filter(Boolean).join(' '))
    .filter(Boolean)
    .join(' ')
}

export function useAttachQuotesToJob(jobId: number, orgId?: number) {
  const { client } = useJobHubDataAdapter()
  const queryClient = useQueryClient()
  const headers = orgId != null ? { 'X-Organisation-Id': String(orgId) } : undefined
  const [skippedError, setSkippedError] = useState<string | null>(null)

  const finish = () => {
    queryClient.invalidateQueries({ queryKey: ['quotes'] })
  }

  const attachFile = useMutation({
    mutationFn: async (file: File) => {
      const body = new FormData()
      body.append('file', file)
      body.append('main_quote', 'false')
      body.append('allow_ai', 'false')
      body.append('source', 'qa')
      await client.post(`/api/v1/jobs/${jobId}/quote-sheets/upload/`, body, { headers, timeout: 180000 })
    },
    onSuccess: finish,
  })

  const attachQDrive = useMutation({
    mutationFn: async (fileRecordId: string) => {
      await client.post(
        `/api/v1/jobs/${jobId}/quote-sheets/from-qdrive/`,
        { file_record_id: fileRecordId, main_quote: false, source: 'qa' },
        { headers, timeout: 180000 },
      )
    },
    onSuccess: finish,
  })

  const attachBuilder = useMutation({
    mutationFn: async ({
      builderQuoteIds,
      includeFamily,
    }: {
      builderQuoteIds: string[]
      includeFamily: boolean
    }) => {
      const { data } = await client.post(
        `/api/v1/jobs/${jobId}/quote-sheets/attach-builder/`,
        {
          builder_quote_ids: builderQuoteIds,
          source: 'qa',
          ...(includeFamily ? { include_family: true } : {}),
        },
        { headers, timeout: 180000 },
      )
      return data
    },
    onSuccess: (data) => {
      finish()
      setSkippedError(skippedMessage(data))
    },
  })

  const searchBuilderQuotes = async (search: string) => {
    const response = await client.get('/api/quotebuilder/quotes/', {
      headers,
      timeout: 180000,
      params: {
        ...(orgId != null ? { org_id: orgId } : {}),
        search: search.trim(),
        include_drafts: '1',
        page_size: 20,
      },
    })
    const data = response.data as unknown
    const rows = Array.isArray(data)
      ? data
      : ((data as { results?: unknown[] })?.results ?? [])
    return (rows as Array<Record<string, unknown>>).flatMap((row) => {
      const id = row.id
      if (id == null) return []
      return [{
        id: String(id),
        quote_number: String(row.quote_number ?? ''),
        status: String(row.status ?? ''),
        revision: Number(row.revision ?? 1) || 1,
        is_latest_revision: row.is_latest_revision !== false,
        job_number: row.job_number ? String(row.job_number) : null,
      }]
    })
  }

  const loadRevisions = async (builderQuoteId: string) => {
    const response = await client.get(
      `/api/v1/jobs/${jobId}/quote-sheets/crm-revisions/`,
      { headers, params: { builder_quote_id: builderQuoteId } },
    )
    const data = response.data as { revisions?: Array<Record<string, unknown>> }
    return (data.revisions ?? []).map((row) => ({
      id: String(row.id),
      quote_number: String(row.quote_number ?? ''),
      revision: Number(row.revision ?? 1) || 1,
      status: String(row.status ?? ''),
      is_latest_revision: row.is_latest_revision !== false,
    }))
  }

  const pending = attachFile.isPending || attachQDrive.isPending || attachBuilder.isPending
  const error = skippedError || qaErrorMessage(
    attachFile.error ?? attachQDrive.error ?? attachBuilder.error,
    '',
  )

  return {
    attachBuilder: attachBuilder.mutateAsync,
    attachFile: attachFile.mutateAsync,
    attachQDrive: attachQDrive.mutateAsync,
    error: error || null,
    pending,
    searchBuilderQuotes,
    loadRevisions,
  }
}
